import {
  VentureOriginationSourceCreateDocument,
  VentureOriginationSourceDeleteDocument,
  VentureOriginationSourceGetByIdDocument,
  VentureOriginationSourceListDocument,
  VentureOriginationSourceUpdateDocument,
  VentureOriginationSourcesPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { PartialNullable } from "config";

import {
  IOriginationSourceCreateVars,
  IOriginationSourceModel,
  IOriginationSourceUpdateVars,
  IOriginationSourceListVars,
  IOriginationSourcesPaginatedResponse,
  IOriginationSourcesPaginatedVars,
  OriginationSourceListResponse,
} from "./types";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

export async function getOriginationSourcesPaginated(
  variables: IOriginationSourcesPaginatedVars
): Promise<IOriginationSourcesPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureOriginationSourcesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureOriginationSourcesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createOriginationSource(
  variables: IOriginationSourceCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureOriginationSourceCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureOriginationSource",
      fieldNames: [
        "ventureOriginationSourcesPaginated",
        "ventureOriginationSourceList",
      ],
    });

    return res?.data?.ventureOriginationSourceCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateOriginationSource(
  variables: IOriginationSourceUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureOriginationSourceUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureOriginationSource",
      id: variables.ventureOriginationSourceId.toString(),
      fieldNames: [
        "ventureOriginationSourcesPaginated",
        "ventureOriginationSourceList",
        "ventureOriginationSourceGetById",
      ],
    });

    return res?.data?.ventureOriginationSourceUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getOriginationSource(
  ventureOriginationSourceId: number
): Promise<PartialNullable<IOriginationSourceModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureOriginationSourceGetByIdDocument,
      variables: {
        ventureOriginationSourceId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureOriginationSourceGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteOriginationSource(
  ventureOriginationSourceId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureOriginationSourceDeleteDocument,
      variables: {
        ventureOriginationSourceId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureOriginationSource",
      id: ventureOriginationSourceId.toString(),
      fieldNames: [
        "ventureOriginationSourcesPaginated",
        "ventureOriginationSourceList",
        "ventureOriginationSourceGetById",
      ],
    });

    return Boolean(res?.data?.ventureOriginationSourceDelete);
  } catch (error) {
    throw error;
  }
}

export async function getOriginationSources(
  variables: IOriginationSourceListVars
): Promise<OriginationSourceListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureOriginationSourceListDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureOriginationSourceList;
  } catch (error) {
    throw error;
  }
}
